import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-month-navigation',
  templateUrl: './month-navigation.component.html',
  styleUrl: './month-navigation.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class MonthNavigationComponent {
  readonly selected_month = input.required<string>();
  readonly display_label = input<string | null>(null);
  readonly current_button_tooltip = input<string>('Ir al mes actual');
  readonly can_go_to_previous_month = input<boolean>(true);
  readonly can_go_to_next_month = input<boolean>(false);
  readonly can_go_to_current_month = input<boolean>(false);

  readonly previous_month = output<void>();
  readonly next_month = output<void>();
  readonly current_month = output<void>();

  readonly selected_month_label = computed((): string => this.display_label() ?? formatMonthLabel(this.selected_month()));
  readonly previous_month_label = computed((): string => formatMonthLabel(getPreviousMonthKey(this.selected_month())));
  readonly next_month_label = computed((): string => formatMonthLabel(getNextMonthKey(this.selected_month())));

  onPreviousMonthClick(): void {
    if (!this.can_go_to_previous_month()) {
      return;
    }
    this.previous_month.emit();
  }

  onNextMonthClick(): void {
    if (!this.can_go_to_next_month()) {
      return;
    }
    this.next_month.emit();
  }

  onCurrentMonthClick(): void {
    if (!this.can_go_to_current_month()) {
      return;
    }
    this.current_month.emit();
  }
}

export function formatMonthLabel(month_key: string): string {
  const month_date = createDateFromMonthKey(month_key);
  if (!month_date) {
    return '';
  }
  const formatted = new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(month_date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function getPreviousMonthKey(month_key: string): string {
  const date = createDateFromMonthKey(month_key) ?? new Date();
  date.setMonth(date.getMonth() - 1);
  return formatMonthKeyFromDate(date);
}

export function getNextMonthKey(month_key: string): string {
  const date = createDateFromMonthKey(month_key) ?? new Date();
  date.setMonth(date.getMonth() + 1);
  return formatMonthKeyFromDate(date);
}

export function normalizeMonthKey(value: string | null): string | null {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) {
    return null;
  }
  const [, month] = value.split('-').map(Number);
  if (month < 1 || month > 12) {
    return null;
  }
  return value;
}

export function formatMonthKeyFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function createDateFromMonthKey(month_key: string): Date | null {
  const normalized = normalizeMonthKey(month_key);
  if (!normalized) {
    return null;
  }
  const [year, month] = normalized.split('-').map(Number);
  const date = new Date();
  date.setFullYear(year, month - 1, 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function shiftMonthKey(month_key: string, delta_months: number): string {
  const date = createDateFromMonthKey(month_key) ?? new Date();
  date.setMonth(date.getMonth() + delta_months);
  return formatMonthKeyFromDate(date);
}

export function getLastClosedMonthKey(reference_date: Date = new Date()): string {
  const date = new Date(reference_date);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  date.setMonth(date.getMonth() - 1);
  return formatMonthKeyFromDate(date);
}

export function getLastDayOfMonthFromMonthKey(month_key: string): Date | null {
  const month_start = createDateFromMonthKey(month_key);
  if (!month_start) {
    return null;
  }

  const last_day = new Date(month_start);
  last_day.setMonth(last_day.getMonth() + 1, 0);
  last_day.setHours(0, 0, 0, 0);
  return last_day;
}

/**
 * Current month → today. Past months → last day of that month (day-based snapshots).
 */
export function resolveDashboardDateForMonthKey(month_key: string, today: Date = new Date()): Date {
  const current_month_key = formatMonthKeyFromDate(today);
  if (month_key === current_month_key) {
    const date = new Date(today);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  return getLastDayOfMonthFromMonthKey(month_key) ?? today;
}

export function formatMonthLabelWithTodayHint(month_key: string, today: Date = new Date()): string {
  const label = formatMonthLabel(month_key);
  if (label === '') {
    return '';
  }

  if (month_key === formatMonthKeyFromDate(today)) {
    return `${label} (Hoy)`;
  }

  return label;
}

export function formatMonthPeriodLabel(from_month: string, to_month: string): string {
  const from_date = createDateFromMonthKey(from_month);
  const to_date = createDateFromMonthKey(to_month);
  if (!from_date || !to_date) {
    return '';
  }

  const from_month_label = new Intl.DateTimeFormat('es-ES', { month: 'short' }).format(from_date);
  const to_label = new Intl.DateTimeFormat('es-ES', { month: 'short', year: 'numeric' }).format(to_date);
  const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

  return `${capitalize(from_month_label.replace('.', ''))} – ${capitalize(to_label.replace('.', ''))}`;
}
